import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Live voice / video room over Supabase Realtime.
 *
 *  • Roster + mute/camera/share state → Realtime **Presence**.
 *  • WebRTC signaling (offer/answer/ICE) → Realtime **Broadcast**.
 *  • Audio + camera + screen media → **WebRTC peer-to-peer mesh**.
 *
 * Supabase carries no media — every track (mic, camera, screen) is a WebRTC
 * track streamed directly peer-to-peer, so adding video/screen share is just
 * "add a track and renegotiate", not a new backend. Because both sides can now
 * add tracks at any time, peers use the **perfect-negotiation** pattern to
 * resolve offer glare (one side polite, one impolite, decided by connId).
 *
 * Mesh suits WINR's small rooms; STUN-only here, so production wants a TURN
 * server for symmetric-NAT peers, and an SFU once rooms outgrow ~5 streamers.
 */

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

/** RMS level above which a stream counts as "speaking" (0–1). */
const SPEAKING_THRESHOLD = 0.05;
const QUALITY_STORAGE_KEY = 'winr:voice-video-quality';
const FREE_VIDEO_QUALITY = { height: 720, frameRate: 30 } as const;

export type VoiceVideoHeight = 720 | 1080 | 1440;
export type VoiceFrameRate = 15 | 30 | 60;

export interface VoiceVideoQuality {
  height: VoiceVideoHeight;
  frameRate: VoiceFrameRate;
}

const DEFAULT_VIDEO_QUALITY: VoiceVideoQuality = { height: 720, frameRate: 30 };

export interface VoicePeer {
  connId: string;
  userId: string;
  name: string;
  avatarSrc: string | null;
  muted: boolean;
  speaking: boolean;
  self: boolean;
  cameraOn: boolean;
  sharing: boolean;
  /** Live camera stream (local for self, remote otherwise) — null when off. */
  cameraStream: MediaStream | null;
  /** Live screen-share stream — null when not sharing. */
  screenStream: MediaStream | null;
}

interface PresenceMeta {
  userId: string;
  name: string;
  avatarSrc: string | null;
  muted: boolean;
  cameraOn: boolean;
  sharing: boolean;
  /** msid of the mic+camera stream, so receivers can tag the camera video. */
  camStreamId: string | null;
  /** msid of the screen stream, to distinguish it from the camera video. */
  screenStreamId: string | null;
}

interface PeerState {
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
}

/** Roster entry — VoicePeer plus the advertised msids used to resolve streams. */
interface RosterEntry extends VoicePeer {
  camStreamId: string | null;
  screenStreamId: string | null;
}

export interface VoiceSelf {
  id: string;
  name: string;
  avatarSrc?: string | null;
}

/** An active voice connection. Lives in the app-level provider so the call
 *  persists while the user navigates the rest of the site. */
export interface VoiceSession {
  communityId: string;
  channelId: string;
  channelName: string;
  self: VoiceSelf;
}

type Status = 'idle' | 'connecting' | 'connected' | 'error';

const isVoiceVideoHeight = (value: unknown): value is VoiceVideoHeight =>
  value === 720 || value === 1080 || value === 1440;

const isVoiceFrameRate = (value: unknown): value is VoiceFrameRate =>
  value === 15 || value === 30 || value === 60;

const canUseVideoQuality = (quality: VoiceVideoQuality, isWinrPlus: boolean) =>
  isWinrPlus ||
  (quality.height <= FREE_VIDEO_QUALITY.height &&
    quality.frameRate <= FREE_VIDEO_QUALITY.frameRate);

const clampVideoQuality = (quality: VoiceVideoQuality, isWinrPlus: boolean): VoiceVideoQuality =>
  canUseVideoQuality(quality, isWinrPlus) ? quality : DEFAULT_VIDEO_QUALITY;

const readStoredVideoQuality = (): VoiceVideoQuality => {
  if (typeof window === 'undefined') return DEFAULT_VIDEO_QUALITY;
  try {
    const raw = window.localStorage.getItem(QUALITY_STORAGE_KEY);
    if (!raw) return DEFAULT_VIDEO_QUALITY;
    const parsed = JSON.parse(raw) as Partial<VoiceVideoQuality>;
    if (!isVoiceVideoHeight(parsed.height) || !isVoiceFrameRate(parsed.frameRate))
      return DEFAULT_VIDEO_QUALITY;
    return { height: parsed.height, frameRate: parsed.frameRate };
  } catch {
    return DEFAULT_VIDEO_QUALITY;
  }
};

const videoConstraintsFor = (quality: VoiceVideoQuality): MediaTrackConstraints => ({
  width: { ideal: Math.round((quality.height * 16) / 9) },
  height: { ideal: quality.height },
  frameRate: { ideal: quality.frameRate, max: quality.frameRate },
});

/**
 * Voice engine. Holds at most one active {@link VoiceSession}; `join` switches
 * channels, `leave` ends the call. Mount this once (in VoiceProvider) so the
 * connection survives route changes — the UI (stage + dock) only reads from it.
 */
export function useVoiceRoom() {
  // TODO: replace with the real WINR+ subscription flag once that product table/API exists.
  const isWinrPlus = false;
  const [session, setSession] = useState<VoiceSession | null>(null);
  const [stageOpen, setStageOpen] = useState(false);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [speakingIds, setSpeakingIds] = useState<Set<string>>(new Set());
  const [muted, setMutedState] = useState(true);
  const [cameraOn, setCameraOnState] = useState(false);
  const [sharing, setSharingState] = useState(false);
  const [videoQuality, setVideoQualityState] = useState<VoiceVideoQuality>(() =>
    clampVideoQuality(readStoredVideoQuality(), isWinrPlus),
  );
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever remote tracks arrive/leave, to recompute participant streams.
  const [streamsVersion, setStreamsVersion] = useState(0);

  const connIdRef = useRef<string>('');

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null); // mic (+ camera when on)
  const screenStreamRef = useRef<MediaStream | null>(null);
  const peersRef = useRef<Map<string, PeerState>>(new Map());
  const pendingIceRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const remoteStreamsRef = useRef<Map<string, Map<string, MediaStream>>>(new Map());
  const audioElsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const analysersRef = useRef<Map<string, AnalyserNode>>(new Map());
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const mutedRef = useRef(true);
  const cameraRef = useRef(false);
  const sharingRef = useRef(false);
  const videoQualityRef = useRef(videoQuality);
  const selfRef = useRef<VoiceSelf | null>(null);
  selfRef.current = session?.self ?? null;
  videoQualityRef.current = videoQuality;

  const bumpStreams = useCallback(() => setStreamsVersion((v) => v + 1), []);

  const presenceMeta = useCallback((): PresenceMeta => {
    const s = selfRef.current;
    return {
      userId: s?.id ?? '',
      name: s?.name ?? 'You',
      avatarSrc: s?.avatarSrc ?? null,
      muted: mutedRef.current,
      cameraOn: cameraRef.current,
      sharing: sharingRef.current,
      camStreamId: localStreamRef.current?.id ?? null,
      screenStreamId: sharingRef.current ? (screenStreamRef.current?.id ?? null) : null,
    };
  }, []);

  const syncPresence = useCallback(() => {
    void channelRef.current?.track(presenceMeta());
  }, [presenceMeta]);

  const sendSignal = useCallback((to: string, kind: string, data: unknown) => {
    void channelRef.current?.send({
      type: 'broadcast',
      event: 'signal',
      payload: { from: connIdRef.current, to, kind, data },
    });
  }, []);

  // ── Speaking detection (local analysis of streams we already receive) ───────
  const attachAnalyser = useCallback((connId: string, stream: MediaStream) => {
    if (analysersRef.current.has(connId) || stream.getAudioTracks().length === 0) return;
    try {
      audioCtxRef.current ??= new AudioContext();
      const ctx = audioCtxRef.current;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analysersRef.current.set(connId, analyser);
    } catch {
      /* AudioContext may be gesture-gated — non-fatal. */
    }
  }, []);

  // ── Local track plumbing ──────────────────────────────────────────────────
  const localTracks = useCallback((): {
    track: MediaStreamTrack;
    stream: MediaStream;
  }[] => {
    const out: { track: MediaStreamTrack; stream: MediaStream }[] = [];
    const mic = localStreamRef.current;
    if (mic) mic.getTracks().forEach((track) => out.push({ track, stream: mic }));
    const screen = screenStreamRef.current;
    if (screen) screen.getTracks().forEach((track) => out.push({ track, stream: screen }));
    return out;
  }, []);

  const addLocalTrack = useCallback((track: MediaStreamTrack, stream: MediaStream) => {
    for (const { pc } of peersRef.current.values()) pc.addTrack(track, stream);
  }, []);

  const removeLocalTrack = useCallback((track: MediaStreamTrack) => {
    for (const { pc } of peersRef.current.values()) {
      const sender = pc.getSenders().find((s) => s.track === track);
      if (sender) pc.removeTrack(sender);
    }
  }, []);

  // ── Peer connections (perfect negotiation) ─────────────────────────────────
  const getOrCreatePeer = useCallback(
    (remoteConnId: string): PeerState => {
      const existing = peersRef.current.get(remoteConnId);
      if (existing) return existing;

      const pc = new RTCPeerConnection(RTC_CONFIG);
      const state: PeerState = {
        pc,
        polite: connIdRef.current > remoteConnId,
        makingOffer: false,
        ignoreOffer: false,
      };
      peersRef.current.set(remoteConnId, state);

      localTracks().forEach(({ track, stream }) => pc.addTrack(track, stream));

      pc.onnegotiationneeded = async () => {
        try {
          state.makingOffer = true;
          await pc.setLocalDescription();
          sendSignal(remoteConnId, 'desc', pc.localDescription);
        } catch {
          /* ignore — a later event will renegotiate */
        } finally {
          state.makingOffer = false;
        }
      };

      pc.onicecandidate = (e) => {
        if (e.candidate) sendSignal(remoteConnId, 'ice', e.candidate.toJSON());
      };

      pc.ontrack = (e) => {
        const [stream] = e.streams;
        if (!stream) return;
        let m = remoteStreamsRef.current.get(remoteConnId);
        if (!m) {
          m = new Map();
          remoteStreamsRef.current.set(remoteConnId, m);
        }
        m.set(stream.id, stream);
        if (e.track.kind === 'audio') {
          let el = audioElsRef.current.get(remoteConnId);
          if (!el) {
            el = new Audio();
            el.autoplay = true;
            audioElsRef.current.set(remoteConnId, el);
          }
          el.srcObject = stream;
          void el.play().catch(() => {});
          attachAnalyser(remoteConnId, stream);
        }
        e.track.onended = bumpStreams;
        bumpStreams();
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'failed') pc.restartIce();
      };

      return state;
    },
    [attachAnalyser, bumpStreams, localTracks, sendSignal],
  );

  const closePeer = useCallback(
    (remoteConnId: string) => {
      peersRef.current.get(remoteConnId)?.pc.close();
      peersRef.current.delete(remoteConnId);
      pendingIceRef.current.delete(remoteConnId);
      analysersRef.current.delete(remoteConnId);
      remoteStreamsRef.current.delete(remoteConnId);
      const el = audioElsRef.current.get(remoteConnId);
      if (el) {
        el.srcObject = null;
        audioElsRef.current.delete(remoteConnId);
      }
      bumpStreams();
    },
    [bumpStreams],
  );

  const flushIce = useCallback(async (remoteConnId: string, pc: RTCPeerConnection) => {
    const queued = pendingIceRef.current.get(remoteConnId);
    if (!queued) return;
    pendingIceRef.current.delete(remoteConnId);
    for (const cand of queued) await pc.addIceCandidate(cand).catch(() => {});
  }, []);

  // ── Connect / teardown ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!session) {
      setStatus('idle');
      return;
    }
    const { communityId, channelId, self } = session;

    let cancelled = false;
    const myConnId = `${self.id}-${Math.random().toString(36).slice(2, 9)}`;
    connIdRef.current = myConnId;
    mutedRef.current = true;
    cameraRef.current = false;
    sharingRef.current = false;
    setMutedState(true);
    setCameraOnState(false);
    setSharingState(false);
    setError(null);
    setStatus('connecting');

    const channel = supabase.channel(`voice:${communityId}:${channelId}`, {
      config: { presence: { key: myConnId }, broadcast: { self: false } },
    });
    channelRef.current = channel;

    const rebuildRoster = () => {
      const stateMap = channel.presenceState<PresenceMeta>();
      const peers: RosterEntry[] = [];
      for (const [connId, metas] of Object.entries(stateMap)) {
        const meta = metas[0];
        if (!meta) continue;
        peers.push({
          connId,
          userId: meta.userId,
          name: meta.name,
          avatarSrc: meta.avatarSrc,
          muted: meta.muted,
          speaking: false,
          self: connId === myConnId,
          cameraOn: meta.cameraOn,
          sharing: meta.sharing,
          cameraStream: null,
          screenStream: null,
          camStreamId: meta.camStreamId,
          screenStreamId: meta.screenStreamId,
        });
      }
      peers.sort((a, b) => (a.self ? -1 : b.self ? 1 : a.name.localeCompare(b.name)));
      setRoster(peers);

      const remoteIds = new Set(peers.map((p) => p.connId).filter((id) => id !== myConnId));
      for (const id of remoteIds) if (!peersRef.current.has(id)) getOrCreatePeer(id);
      for (const id of [...peersRef.current.keys()]) if (!remoteIds.has(id)) closePeer(id);
    };

    channel
      .on('presence', { event: 'sync' }, rebuildRoster)
      .on('broadcast', { event: 'signal' }, async (msg) => {
        const { from, to, kind, data } = (msg.payload ?? {}) as {
          from: string;
          to: string;
          kind: string;
          data: unknown;
        };
        if (to !== myConnId || from === myConnId) return;
        const state = getOrCreatePeer(from);
        const { pc } = state;

        if (kind === 'desc') {
          const description = data as RTCSessionDescriptionInit;
          const collision =
            description.type === 'offer' && (state.makingOffer || pc.signalingState !== 'stable');
          state.ignoreOffer = !state.polite && collision;
          if (state.ignoreOffer) return;
          await pc.setRemoteDescription(description).catch(() => {});
          await flushIce(from, pc);
          if (description.type === 'offer') {
            await pc.setLocalDescription();
            sendSignal(from, 'desc', pc.localDescription);
          }
        } else if (kind === 'ice') {
          const cand = data as RTCIceCandidateInit;
          if (pc.remoteDescription) {
            await pc.addIceCandidate(cand).catch(() => {
              if (!state.ignoreOffer) {
                /* swallow */
              }
            });
          } else {
            const q = pendingIceRef.current.get(from) ?? [];
            q.push(cand);
            pendingIceRef.current.set(from, q);
          }
        }
      });

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        localStreamRef.current = stream;
        stream.getAudioTracks().forEach((t) => (t.enabled = false)); // join muted
        attachAnalyser(myConnId, stream);
      } catch {
        if (!cancelled) setError('Microphone unavailable — you can still listen.');
      }

      await channel.subscribe((subStatus) => {
        if (subStatus === 'SUBSCRIBED') {
          void channel.track(presenceMeta());
          setStatus('connected');
        } else if (subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT') {
          setStatus('error');
          setError('Lost connection to voice.');
        }
      });
    })();

    let frame = 0;
    const buf = new Float32Array(256);
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      if (frame++ % 6 !== 0) return;
      const next = new Set<string>();
      for (const [connId, analyser] of analysersRef.current) {
        if (connId === myConnId && mutedRef.current) continue;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = buf[i] ?? 0;
          sum += v * v;
        }
        if (Math.sqrt(sum / buf.length) > SPEAKING_THRESHOLD) next.add(connId);
      }
      setSpeakingIds((prev) => {
        if (prev.size === next.size && [...next].every((id) => prev.has(id))) return prev;
        return next;
      });
    };
    rafRef.current = requestAnimationFrame(tick);

    // Tab visibility: keep media alive across tab switches. We never disable the
    // camera on blur — but some browsers UA-pause playback and the AudioContext
    // when backgrounded, so on return we resume audio and re-assert our own
    // track state (camera stays on; mic respects the live mute toggle).
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void audioCtxRef.current?.resume().catch(() => {});
      for (const el of audioElsRef.current.values()) void el.play().catch(() => {});
      localStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = true));
      localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !mutedRef.current));
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- read live at unmount, not a DOM node ref
      for (const id of [...peersRef.current.keys()]) closePeer(id);
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
      screenStreamRef.current?.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
      void audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- read live at unmount, not a DOM node ref
      analysersRef.current.clear();
      void supabase.removeChannel(channel);
      channelRef.current = null;
      setRoster([]);
      setSpeakingIds(new Set());
      setStatus('idle');
    };
  }, [session, attachAnalyser, closePeer, flushIce, getOrCreatePeer, presenceMeta, sendSignal]);

  // ── Controls ────────────────────────────────────────────────────────────────
  const setMuted = useCallback(
    (next: boolean) => {
      mutedRef.current = next;
      setMutedState(next);
      localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = !next));
      syncPresence();
    },
    [syncPresence],
  );
  const toggleMuted = useCallback(() => setMuted(!mutedRef.current), [setMuted]);

  const saveVideoQuality = useCallback(
    async (next: VoiceVideoQuality) => {
      if (!canUseVideoQuality(next, isWinrPlus)) return false;

      videoQualityRef.current = next;
      setVideoQualityState(next);
      try {
        window.localStorage.setItem(QUALITY_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* storage may be unavailable */
      }

      const tracks = localStreamRef.current?.getVideoTracks() ?? [];
      await Promise.all(
        tracks.map((track) => track.applyConstraints(videoConstraintsFor(next)).catch(() => {})),
      );
      return true;
    },
    [isWinrPlus],
  );

  const setCamera = useCallback(
    async (next: boolean) => {
      if (next === cameraRef.current) return;
      if (next) {
        try {
          const cam = await navigator.mediaDevices.getUserMedia({
            video: videoConstraintsFor(videoQualityRef.current),
          });
          const track = cam.getVideoTracks()[0];
          if (!track) return;
          const stream = localStreamRef.current ?? new MediaStream();
          localStreamRef.current = stream;
          stream.addTrack(track);
          addLocalTrack(track, stream);
          cameraRef.current = true;
          setCameraOnState(true);
        } catch {
          setError('Camera unavailable or permission denied.');
          return;
        }
      } else {
        const stream = localStreamRef.current;
        stream?.getVideoTracks().forEach((t) => {
          removeLocalTrack(t);
          t.stop();
          stream.removeTrack(t);
        });
        cameraRef.current = false;
        setCameraOnState(false);
      }
      syncPresence();
      bumpStreams();
    },
    [addLocalTrack, bumpStreams, removeLocalTrack, syncPresence],
  );
  const toggleCamera = useCallback(() => void setCamera(!cameraRef.current), [setCamera]);

  const setSharing = useCallback(
    async (next: boolean) => {
      if (next === sharingRef.current) return;
      if (next) {
        try {
          const screen = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: false,
          });
          const track = screen.getVideoTracks()[0];
          if (!track) return;
          screenStreamRef.current = screen;
          addLocalTrack(track, screen);
          sharingRef.current = true;
          setSharingState(true);
          // Browser "Stop sharing" UI ends the track out from under us.
          track.onended = () => void setSharing(false);
        } catch {
          return; // user cancelled the picker — not an error
        }
      } else {
        const screen = screenStreamRef.current;
        screen?.getTracks().forEach((t) => {
          removeLocalTrack(t);
          t.stop();
        });
        screenStreamRef.current = null;
        sharingRef.current = false;
        setSharingState(false);
      }
      syncPresence();
      bumpStreams();
    },
    [addLocalTrack, bumpStreams, removeLocalTrack, syncPresence],
  );
  const toggleSharing = useCallback(() => void setSharing(!sharingRef.current), [setSharing]);

  const participants = useMemo<VoicePeer[]>(() => {
    void streamsVersion; // recompute when remote/local streams change
    return roster.map((p) => {
      // For your own tile the live local state is the source of truth — reading
      // your mute/camera/share back off Presence lags the controls and can leave
      // the self tile out of sync with the bottom bar.
      const peerMuted = p.self ? muted : p.muted;
      const peerCameraOn = p.self ? cameraOn : p.cameraOn;
      const peerSharing = p.self ? sharing : p.sharing;

      let cameraStream: MediaStream | null = null;
      let screenStream: MediaStream | null = null;
      if (p.self) {
        cameraStream = cameraOn ? localStreamRef.current : null;
        screenStream = sharing ? screenStreamRef.current : null;
      } else {
        const streams = remoteStreamsRef.current.get(p.connId);
        if (streams) {
          if (p.cameraOn && p.camStreamId) cameraStream = streams.get(p.camStreamId) ?? null;
          if (p.sharing && p.screenStreamId) screenStream = streams.get(p.screenStreamId) ?? null;
        }
      }
      return {
        connId: p.connId,
        userId: p.userId,
        name: p.name,
        avatarSrc: p.avatarSrc,
        muted: peerMuted,
        self: p.self,
        cameraOn: peerCameraOn,
        sharing: peerSharing,
        cameraStream,
        screenStream,
        speaking: speakingIds.has(p.connId) && !peerMuted,
      };
    });
  }, [roster, speakingIds, muted, cameraOn, sharing, streamsVersion]);

  const join = useCallback((next: VoiceSession) => setSession(next), []);
  const leave = useCallback(() => setSession(null), []);

  return {
    session,
    isWinrPlus,
    join,
    leave,
    stageOpen,
    setStageOpen,
    participants,
    muted,
    cameraOn,
    sharing,
    videoQuality,
    saveVideoQuality,
    status,
    error,
    setMuted,
    toggleMuted,
    toggleCamera,
    toggleSharing,
  };
}
